import {
  IconButton,
  SingleSelectDropdown,
  TextInput,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import { useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import type { JsonObject } from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import {
  BUDGET_UNIT_KEY,
  BUDGET_UNIT_OPTIONS,
  RECIPE_FIELD_CATALOG,
  catalogForFamily,
  flattenRecipeScalars,
  overrideDefaultValue,
  type RecipeFamily,
  type RecipeFieldSpec,
} from "./recipe";
import type { KeyValuePair } from "./submitWizardForm";
import styles from "./submitWizard.module.css";

// config override 입력기. 행 목록과 맨 아래 '+ 추가' 메뉴(키/값 행)로 구성한다. 카메라/채널
// mux 는 채널 매핑 섹션(ChannelSelectionEditor)이 담당한다. 여기서는 자유 입력만 받는다.
// 키 후보는 큐레이션 카탈로그(한글 라벨·타입 위젯)와 로드된 config 의 평탄화 스칼라 키(현재 값
// 미리보기)의 합집합이다. key 를 고르면 기본값(추천값 → config 현재 값)을 자동으로 채운다.
const BOOLEAN_OPTIONS: SingleSelectDropdownOption[] = [
  { value: "true", label: "true" },
  { value: "false", label: "false" },
];

// 행 키 드롭다운의 "직접 입력" sentinel(실제 키와 충돌하지 않는 값).
const CUSTOM_KEY_SENTINEL = "__custom_key__";

export function RecipeOverrideEditor({
  pairs,
  onChange,
  family,
  configData,
  ariaLabel,
}: {
  pairs: readonly KeyValuePair[];
  onChange: (next: KeyValuePair[]) => void;
  // 선택된 config 의 계열. null 이면 두 계열 카탈로그 합집합을 쓴다.
  family: RecipeFamily | null;
  // 파싱된 config 트리(키 후보 보충·현재 값 미리보기). 미로드면 null.
  configData: JsonObject | null;
  ariaLabel?: string;
}): ReactNode {
  // override 할 수 있는 카탈로그 필드만 담는다(readOnly·인터폴레이션 마커 제외).
  const specs = (family ? catalogForFamily(family) : RECIPE_FIELD_CATALOG).filter(
    (spec) => spec.readOnly !== true,
  );
  const specByPath = new Map(specs.map((spec) => [spec.path, spec]));
  const configValues = useMemo(
    () => (configData === null ? new Map<string, string>() : flattenRecipeScalars(configData)),
    [configData],
  );
  const candidates = useMemo(() => buildCandidates(specs, configValues), [specs, configValues]);

  const defaultValueFor = (key: string): string => overrideDefaultValue(key, configData ?? {});

  // 키를 바꾸면 값도 그 키의 기본값으로 리셋한다. 이전 값이 새 필드와 맞지 않을 수 있기 때문이다.
  const setKey = (index: number, key: string) =>
    onChange(pairs.map((pair, i) => (i === index ? { key, value: defaultValueFor(key) } : pair)));
  const setValue = (index: number, value: string) =>
    onChange(pairs.map((pair, i) => (i === index ? { ...pair, value } : pair)));
  const remove = (index: number) => onChange(pairs.filter((_, i) => i !== index));

  return (
    <div className={styles.kvEditor} aria-label={ariaLabel}>
      {pairs.map((pair, index) => (
        // 행에 안정적인 id 가 없어서 index 키를 쓴다(추가는 배열 끝, 삭제는 필터라 순서가 유지된다).
        <OverrideRow
          key={index}
          pair={pair}
          candidates={candidates}
          specByPath={specByPath}
          onKeyChange={(key) => setKey(index, key)}
          onValueChange={(value) => setValue(index, value)}
          onRemove={() => remove(index)}
        />
      ))}
      {/* 이 섹션의 주 액션. 고를 항목이 하나뿐이라 메뉴가 아니라 버튼이다. */}
      <IconButton
        text="+ override 추가"
        variant="solid"
        className={styles.addMenu}
        onClick={() => onChange([...pairs, { key: "", value: "" }])}
      />
    </div>
  );
}

// override 한 행. key(검색 드롭다운, '직접 입력…'을 고르면 텍스트 입력으로 전환), 값 위젯, 삭제로 구성한다.
function OverrideRow({
  pair,
  candidates,
  specByPath,
  onKeyChange,
  onValueChange,
  onRemove,
}: {
  pair: KeyValuePair;
  candidates: SingleSelectDropdownOption[];
  specByPath: Map<string, RecipeFieldSpec>;
  onKeyChange: (key: string) => void;
  onValueChange: (value: string) => void;
  onRemove: () => void;
}): ReactNode {
  // 후보 밖 키를 가진 채 mount 되면(직접 입력한 행) 텍스트 입력 모드를 유지한다.
  const [custom, setCustom] = useState(
    () => pair.key !== "" && !candidates.some((option) => option.value === pair.key),
  );
  const selectKey = (key: string) => {
    if (key === CUSTOM_KEY_SENTINEL) {
      setCustom(true);
      return;
    }
    onKeyChange(key);
  };
  return (
    <div className={`${styles.kvRow} ${styles.kvRowFixed}`}>
      {custom ? (
        <TextInput
          value={pair.key}
          placeholder="config key를 입력해 주세요(예: train.custom.flag)."
          aria-label="custom config key"
          onChange={(event: ChangeEvent<HTMLInputElement>) => onKeyChange(event.target.value)}
        />
      ) : (
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="필드"
          placeholder="config key를 검색하거나 선택해 주세요."
          value={pair.key === "" ? null : pair.key}
          options={[
            ...rowKeyOptions(candidates, pair.key),
            {
              value: CUSTOM_KEY_SENTINEL,
              label: "직접 입력…",
              description: "목록에 없는 키를 직접 입력합니다.",
            },
          ]}
          onValueChange={selectKey}
          searchable
          searchPlaceholder="config key를 검색해 보세요."
        />
      )}
      <OverrideValueInput
        spec={specForKey(pair.key, specByPath)}
        value={pair.value}
        onChange={onValueChange}
      />
      <IconButton
        text="✕"
        variant="surface"
        className={styles.squareButton}
        aria-label="행 삭제"
        onClick={onRemove}
      />
    </div>
  );
}

// 값 위젯. enum/boolean 은 드롭다운, 그 외(숫자·문자열·리스트)는 텍스트다. spec 을 모르면 텍스트다.
function OverrideValueInput({
  spec,
  value,
  onChange,
}: {
  spec: RecipeFieldSpec | undefined;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  if (spec?.type === "enum" && spec.enumOptions) {
    return (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="값"
        placeholder="값을 선택해 주세요."
        value={value === "" ? null : value}
        options={spec.enumOptions.map((option) => ({ value: option, label: option }))}
        onValueChange={onChange}
      />
    );
  }
  if (spec?.type === "boolean") {
    return (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="값"
        placeholder="값을 선택해 주세요."
        value={value === "" ? null : value}
        options={BOOLEAN_OPTIONS}
        onValueChange={onChange}
      />
    );
  }
  return (
    <TextInput
      value={value}
      placeholder={valuePlaceholder(spec)}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    />
  );
}

// 카탈로그 spec 을 먼저 쓴다. 카탈로그 밖 특수 키(학습 길이 단위)는 pseudo-spec 으로 타입 위젯을 준다.
function specForKey(
  key: string,
  specByPath: Map<string, RecipeFieldSpec>,
): RecipeFieldSpec | undefined {
  if (key === BUDGET_UNIT_KEY) {
    return {
      path: BUDGET_UNIT_KEY,
      section: "train",
      label: "Training 길이 단위",
      type: "enum",
      enumOptions: BUDGET_UNIT_OPTIONS,
    };
  }
  return specByPath.get(key);
}

// 후보 옵션은 카탈로그(한글 라벨 + 현재 값)와 config 평탄화 키(현재 값)의 합집합이다. 카탈로그를 먼저 정렬한다.
function buildCandidates(
  specs: readonly RecipeFieldSpec[],
  configValues: ReadonlyMap<string, string>,
): SingleSelectDropdownOption[] {
  const options: SingleSelectDropdownOption[] = specs.map((spec) => ({
    value: spec.path,
    label: spec.path,
    description: withCurrentValue(spec.label, configValues.get(spec.path)),
  }));
  const known = new Set(specs.map((spec) => spec.path));
  const extras = [...configValues.keys()].filter((key) => !known.has(key)).sort();
  for (const key of extras) {
    options.push({ value: key, label: key, description: `현재 ${configValues.get(key)}` });
  }
  return options;
}

function withCurrentValue(label: string, current: string | undefined): string {
  return current === undefined ? label : `${label} · 현재 ${current}`;
}

// 행의 키가 후보 밖(다른 계열·직접 입력)이면 그 키도 옵션에 남겨 표시한다.
function rowKeyOptions(
  candidates: SingleSelectDropdownOption[],
  currentKey: string,
): SingleSelectDropdownOption[] {
  if (currentKey !== "" && !candidates.some((option) => option.value === currentKey)) {
    return [
      { value: currentKey, label: currentKey, description: "직접 입력한 키입니다." },
      ...candidates,
    ];
  }
  return candidates;
}

function valuePlaceholder(spec: RecipeFieldSpec | undefined): string {
  if (spec === undefined) {
    return "값을 입력해 주세요.";
  }
  if (spec.type === "list") {
    return "목록을 입력해 주세요(예: [0.95, 0.999]).";
  }
  if (spec.type === "number" || spec.type === "integer") {
    return spec.nullable ? "숫자 또는 null을 입력해 주세요." : "숫자를 입력해 주세요(예: 1e-5).";
  }
  return spec.nullable ? "값 또는 null을 입력해 주세요." : "값을 입력해 주세요.";
}
